import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { StepTypeSchema } from './step.schema.js';
import { LANGUAGE_CODES, DEFAULT_LANGUAGE } from '../constants/languages.js';
// Extend Zod with OpenAPI
extendZodWithOpenApi(z);
// Course progression type enum
export const CourseProgressionTypeSchema = z.enum(['lesson', 'topic', 'module']).openapi({
    description: 'Type of progression tracking for the course',
    example: 'lesson',
});
export const CourseVisibilitySchema = z.enum(['public', 'private', 'unlisted']).openapi({
    description: 'Course visibility level',
    example: 'public',
});
// UUID validation
const uuidSchema = z.string().uuid('Invalid UUID format');
// Create course input schema
export const CreateCourseSchema = z
    .object({
    title: z
        .string()
        .min(1, 'Title is required')
        .max(255, 'Title must be less than 255 characters')
        .trim()
        .openapi({
        description: 'Course title',
        example: 'Introduction to React',
    }),
    slug: z
        .string()
        .max(255, 'Slug must be less than 255 characters')
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase alphanumeric with hyphens only')
        .trim()
        .or(z.literal(''))
        .nullable()
        .optional()
        .transform((val) => (val === '' ? null : val))
        .openapi({
        description: 'URL-friendly course identifier (optional)',
        example: 'introduction-to-react',
    }),
    description: z
        .string()
        .max(5000, 'Description must be less than 5000 characters')
        .nullable()
        .optional()
        .openapi({
        description: 'Detailed course description',
        example: 'Learn React from the ground up with practical examples',
    }),
    progressionType: CourseProgressionTypeSchema.optional().default('lesson').openapi({
        description: 'Type of progression tracking (lesson or module)',
        example: 'lesson',
    }),
    editorIds: z
        .array(z.string().uuid())
        .max(20, 'Maximum 20 editors allowed')
        .optional()
        .default([])
        .openapi({ description: 'Optional user IDs to add as editors on creation' }),
    visibility: CourseVisibilitySchema.optional().default('public').openapi({
        description: 'Course visibility (public, private, or unlisted)',
        example: 'public',
    }),
})
    .openapi('CreateCourse');
// Nested lesson schema for batch content save
const NestedLessonSchema = z.object({
    id: z.string().optional().openapi({
        description: 'Lesson ID (omit for new, include for existing). IDs starting with "temp-" are treated as new.',
    }),
    title: z.string().min(1, 'Lesson title is required').max(255).trim(),
    description: z.string().max(5000).nullable().optional(),
    isLive: z.boolean().optional(),
});
// Nested topic schema for batch content save
const NestedTopicSchema = z.object({
    id: z.string().optional().openapi({
        description: 'Topic ID (omit for new, include for existing). IDs starting with "temp-" are treated as new.',
    }),
    title: z.string().min(1, 'Topic title is required').max(255).trim(),
    description: z.string().max(5000).nullable().optional(),
    lessons: z.array(NestedLessonSchema).optional().default([]),
});
// Nested module schema for batch content save
const NestedModuleSchema = z.object({
    id: z.string().optional().openapi({
        description: 'Module ID (omit for new, include for existing). IDs starting with "temp-" are treated as new.',
    }),
    title: z.string().min(1, 'Module title is required').max(255).trim(),
    description: z.string().max(5000).nullable().optional(),
    topics: z.array(NestedTopicSchema).optional().default([]),
});
// Update course input schema (all fields optional)
export const UpdateCourseSchema = z
    .object({
    title: z
        .string()
        .min(1, 'Title cannot be empty')
        .max(255, 'Title must be less than 255 characters')
        .trim()
        .optional()
        .openapi({
        description: 'Updated course title',
        example: 'Advanced React Patterns',
    }),
    slug: z
        .string()
        .max(255, 'Slug must be less than 255 characters')
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase alphanumeric with hyphens only')
        .trim()
        .or(z.literal(''))
        .nullable()
        .optional()
        .transform((val) => (val === '' ? null : val))
        .openapi({
        description: 'Updated URL-friendly course identifier',
        example: 'advanced-react-patterns',
    }),
    description: z
        .string()
        .max(5000, 'Description must be less than 5000 characters')
        .nullable()
        .optional()
        .openapi({
        description: 'Updated course description',
    }),
    featured: z.boolean().optional().openapi({
        description: 'Whether the course is featured in the store',
    }),
    headerImageUrl: z.string().nullish().openapi({
        description: 'Storage path for the course header/banner image',
        example: 'courses/abc/header/image.jpg',
    }),
    iconUrl: z.string().nullish().openapi({
        description: 'Storage path for the course icon/thumbnail image',
        example: 'courses/abc/icon/icon.png',
    }),
    accentColour: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Accent colour must be a valid hex colour (e.g. #FF5733)')
        .nullish()
        .openapi({
        description: 'Hex colour code used as the course accent colour',
        example: '#FF5733',
    }),
    progressionType: CourseProgressionTypeSchema.optional().openapi({
        description: 'Updated progression tracking type',
        example: 'module',
    }),
    ownerId: z.string().uuid('Invalid UUID format').nullable().optional().openapi({
        description: 'User ID of the course owner (for transferring ownership)',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
    editorIds: z
        .array(z.string().uuid())
        .max(20, 'Maximum 20 editors allowed')
        .optional()
        .openapi({ description: 'User IDs to set as editors (replaces existing editors)' }),
    moduleIds: z
        .array(uuidSchema)
        .optional()
        .openapi({
        description: 'List of module IDs associated with the course (flat mode)',
        example: ['550e8400-e29b-41d4-a716-446655440000'],
    }),
    modules: z.array(NestedModuleSchema).optional().openapi({
        description: 'Nested content tree for batch create/update. When present, replaces moduleIds. Items without an id (or with a temp- prefix id) are created; existing items are updated.',
    }),
    categoryIds: z
        .array(uuidSchema)
        .optional()
        .openapi({
        description: 'List of category IDs to assign to the course',
        example: ['b2968ed3-7e79-476f-8e70-66df6dfa217d'],
    }),
    tagIds: z
        .array(uuidSchema)
        .optional()
        .openapi({
        description: 'List of tag IDs to assign to the course',
        example: ['a1b2c3d4-e5f6-7890-abcd-ef1234567890'],
    }),
    visibility: CourseVisibilitySchema.optional().openapi({
        description: 'Updated course visibility',
    }),
})
    .openapi('UpdateCourse');
// List courses query parameters
export const ListCoursesQuerySchema = z.object({
    page: z
        .string()
        .optional()
        .default('1')
        .transform((val) => parseInt(val, 10))
        .refine((val) => val > 0, 'Page must be greater than 0'),
    pageSize: z
        .string()
        .optional()
        .default('10')
        .transform((val) => parseInt(val, 10))
        .refine((val) => val > 0 && val <= 100, 'Page size must be between 1 and 100'),
    sortBy: z.enum(['createdAt', 'updatedAt', 'title', 'learnerCount']).optional().default('createdAt').openapi({
        description: 'Field to sort results by',
        example: 'createdAt',
    }),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc').openapi({
        description: 'Sort direction',
        example: 'desc',
    }),
    includeDeleted: z
        .string()
        .optional()
        .default('false')
        .transform((val) => val === 'true'),
    search: z.string().max(255, 'Search query must be less than 255 characters').optional().openapi({
        description: 'Free-text search query to filter courses by title or description',
        example: 'react',
    }),
    categoryId: z.string().uuid('Invalid category UUID').optional().openapi({
        description: 'Filter courses by category ID',
        example: 'b2968ed3-7e79-476f-8e70-66df6dfa217d',
    }),
    tags: z.string().optional().openapi({
        description: 'Comma-separated list of tag slugs to filter courses by',
        example: 'python,beginner',
    }),
    featured: z
        .string()
        .optional()
        .transform((val) => (val === undefined ? undefined : val === 'true')),
    isPublished: z
        .string()
        .optional()
        .transform((val) => (val === undefined ? undefined : val === 'true'))
        .openapi({
        description: 'Filter courses by published/unpublished status',
    }),
    ownerId: z.string().uuid('Invalid owner UUID').optional().openapi({
        description: 'Filter courses by owner user ID',
    }),
    visibility: CourseVisibilitySchema.optional().openapi({
        description: 'Filter courses by visibility level',
    }),
    excludeEnrolled: z
        .string()
        .optional()
        .transform((val) => (val === undefined ? undefined : val === 'true'))
        .openapi({
        description: 'Exclude courses the authenticated user is enrolled in',
    }),
});
// Course ID parameter validation
export const CourseIdParamSchema = z.object({
    courseId: uuidSchema.openapi({
        description: 'Unique identifier of the course',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
});
// Get course query parameters (for include)
export const GetCourseQuerySchema = z.object({
    include: z.enum(['modules', 'content']).optional().openapi({
        description: 'Include related data: modules (1 level) or content (full nested structure)',
        example: 'modules',
    }),
});
// Course Schema
export const CourseSchema = z
    .object({
    id: uuidSchema.openapi({ description: 'Course UUID' }),
    title: z.string().openapi({ description: 'Course title', example: 'Introduction to Python' }),
    slug: z
        .string()
        .nullable()
        .openapi({ description: 'URL-friendly slug', example: 'introduction-to-python' }),
    description: z.string().nullable().openapi({ description: 'Course description' }),
    isPublished: z
        .boolean()
        .openapi({ description: 'Whether the course is published and visible to learners' }),
    iconUrl: z.string().nullable().openapi({ description: 'URL for the course icon image' }),
    headerImageUrl: z
        .string()
        .nullable()
        .openapi({ description: 'URL for the course header image' }),
    ownerId: uuidSchema.openapi({ description: 'ID of the user who owns the course' }),
    createdBy: uuidSchema.openapi({ description: 'ID of the user who created the course' }),
    createdAt: z.string().openapi({ description: 'Creation timestamp' }),
    updatedAt: z.string().openapi({ description: 'Last update timestamp' }),
    progressionType: CourseProgressionTypeSchema.nullable().openapi({
        description: 'How learners progress through the course (lesson, topic, or module based)',
    }),
    featured: z.boolean().optional().openapi({ description: 'Whether the course is featured' }),
    visibility: CourseVisibilitySchema.openapi({ description: 'Course visibility level' }),
    locked: z.boolean().optional().openapi({ description: 'Whether the course is locked' }),
    accentColour: z.string().nullable().optional().openapi({ description: 'Course accent colour hex code' }),
    categories: z
        .array(z.object({ id: z.string(), name: z.string(), slug: z.string() }))
        .optional()
        .openapi({ description: 'Associated categories' }),
    tags: z
        .array(z.object({ id: z.string(), name: z.string(), slug: z.string() }))
        .optional()
        .openapi({ description: 'Associated tags' }),
    modules: z.array(z.unknown()).optional().openapi({ description: 'Nested modules' }),
    _count: z
        .object({
        modules: z.number(),
        lessons: z.number(),
        learners: z.number(),
    })
        .optional()
        .openapi({ description: 'Aggregate counts for modules, lessons, and active learners' }),
    owner: z
        .object({
        id: z.string(),
        /** Canonical display name from BetterAuth `user.name`. */
        name: z.string().nullable(),
        email: z.string().nullable().optional(),
        avatarUrl: z.string().nullable(),
    })
        .optional()
        .openapi({ description: 'Course owner profile' }),
    editors: z
        .array(z.object({
        id: z.string(),
        /** Canonical display name from BetterAuth `user.name`. */
        name: z.string().nullable(),
        email: z.string().nullable().optional(),
        avatarUrl: z.string().nullable(),
    }))
        .optional()
        .openapi({ description: 'Course editor profiles' }),
})
    .openapi('Course');
// Course Category Schema
export const CourseCategorySchema = z
    .object({
    id: uuidSchema.openapi({ description: 'Category UUID' }),
    name: z.string().openapi({ description: 'Category name', example: 'Programming' }),
    slug: z.string().openapi({ description: 'URL-friendly slug', example: 'programming' }),
    createdAt: z.string().openapi({ description: 'Creation timestamp' }),
    updatedAt: z.string().openapi({ description: 'Last update timestamp' }),
})
    .openapi('CourseCategory');
// ===== AI Course Generation Schemas =====
export const GenerateCourseRequestSchema = z
    .object({
    prompt: z.string().min(10).max(10000).openapi({
        description: 'Natural language description of the course to generate',
        example: 'Create a beginner Python programming course covering variables, loops, and functions',
    }),
    fileContexts: z
        .array(z.object({
        filename: z.string(),
        text: z.string().max(50000),
    }))
        .max(10)
        .optional()
        .openapi({ description: 'Optional file contents to provide context for generation' }),
    settings: z
        .object({
        progressionType: z.enum(['lesson', 'topic', 'module']).default('lesson'),
        language: z.enum(LANGUAGE_CODES).default(DEFAULT_LANGUAGE),
        excludedStepTypes: z.array(StepTypeSchema).optional(),
    })
        .optional(),
})
    .openapi('GenerateCourseRequest');
export const ResumeGenerationParamSchema = z.object({
    jobId: z.string().uuid().openapi({
        description: 'Background generation job UUID',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
});
export const ReportQuerySchema = z
    .object({
    format: z.enum(['json', 'csv']).optional().default('json').openapi({
        description: 'Report output format',
        example: 'json',
    }),
})
    .openapi('ReportQuery');
export const RefineStructureSchema = z
    .object({
    feedback: z
        .string()
        .min(1, 'Feedback is required')
        .max(5000, 'Feedback must be less than 5000 characters')
        .trim()
        .openapi({
        description: 'Feedback describing desired changes to the proposed structure',
        example: 'Add more exercises and reduce the number of info steps',
    }),
})
    .openapi('RefineStructure');
//# sourceMappingURL=course.schema.js.map